import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { ROLES, getFlagForCountry, getYardLocations, getYardsByCountry, DEFAULT_YARD_LOCATIONS, getZoneAllocations, getDestinationCountries, DEFAULT_DESTINATION_COUNTRIES } from '../data/constants';
import { useLanguage } from '../translations/LanguageContext';
import { DownloadIcon, XIcon, CheckIcon, PlusIcon, EditIcon, DeleteIcon, SearchIcon } from './Icons';
import { logActivity } from '../data/notifications';
import { PARTS_CATALOG, PARTS_CATALOG_CATEGORIES } from '../data/partsCatalog';
import generateBrandedQR from '../utils/generateBrandedQR';

function Settings({ user, lang, onUpdateUser }) {
  const { t } = useLanguage();
  const role = ROLES[user.role] || {};
  const isCustomer = role.isCustomer;

  const [form, setForm] = useState({
    name:     user.name     || '',
    email:    user.email    || '',
    phone:    user.phone    || '',
    password: '',
    avatar:   user.avatar   || '',
  });
  const [saved,  setSaved]  = useState(false);
  const [pwShow, setPwShow] = useState(false);
  const avatarRef = useRef();

  const updateField = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = ev => updateField('avatar', ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (isCustomer) {
      // Save to customers table
      const customers = DB.get('customers') || [];
      const updated = {
        ...user,
        name:   form.name.trim()  || user.name,
        phone:  form.phone.trim(),
        avatar: form.avatar,
        ...(form.password ? { password: form.password } : {}),
      };
      DB.set('customers', customers.map(c => c.id === updated.id ? updated : c));
      onUpdateUser?.(updated);

      // Notify admin about the change
      const changes = [];
      if (form.password) changes.push('password');
      if (form.name.trim() !== user.name) changes.push('name');
      if (form.phone.trim() !== (user.phone || '')) changes.push('phone');
      if (form.avatar !== (user.avatar || '')) changes.push('profile photo');
      if (changes.length > 0) {
        logActivity.customerSettingsChanged?.(user.name, changes.join(', '));
      }
    } else {
      // Save to users table (staff)
      const users = DB.get('users') || [];
      const updated = {
        ...user,
        name:   form.name.trim()  || user.name,
        email:  form.email.trim() || user.email,
        phone:  form.phone.trim(),
        avatar: form.avatar,
        ...(form.password ? { password: form.password } : {}),
      };
      DB.set('users', users.map(u => u.id === updated.id ? updated : u));
      onUpdateUser?.(updated);
    }

    setForm(prev => ({ ...prev, password: '' }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleExport = () => {
    const data = DB.export();
    const blob = new Blob([data], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'bigstar_backup.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all data? This cannot be undone.')) {
      await DB.reset();
      window.location.reload();
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 20 }}>{t('settings')}</h2>

      {/* ── My Profile ─────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title">My Profile</div>
        </div>
        <div className="card-body">
          <form onSubmit={handleSave}>
            <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>

              {/* Avatar upload */}
              <div className="profile-avatar-wrap">
                <div
                  className="profile-pic"
                  onClick={() => avatarRef.current.click()}
                  title="Click to change photo"
                >
                  {form.avatar
                    ? <img src={form.avatar} alt="avatar" />
                    : (form.name || user.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="profile-pic-hint">Click to change photo</div>
                {form.avatar && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => updateField('avatar', '')}
                    style={{ fontSize: 11 }}
                  >
                    Remove photo
                  </button>
                )}
                <input
                  ref={avatarRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarUpload}
                />
              </div>

              {/* Fields */}
              <div style={{ flex: 1, minWidth: 240 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('name')}</label>
                    <input
                      className="form-input"
                      value={form.name}
                      onChange={e => updateField('name', e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('phone')}</label>
                    <input
                      className="form-input"
                      value={form.phone}
                      onChange={e => updateField('phone', e.target.value)}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                {!isCustomer && (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Login / {t('email')}</label>
                      <input
                        className="form-input"
                        value={form.email}
                        onChange={e => updateField('email', e.target.value)}
                        placeholder="username or email"
                        autoComplete="username"
                      />
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      New {t('password')}
                      <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 6 }}>
                        (leave blank to keep current)
                      </span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-input"
                        type={pwShow ? 'text' : 'password'}
                        value={form.password}
                        onChange={e => updateField('password', e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        style={{ paddingRight: 48 }}
                      />
                      <button
                        type="button"
                        onClick={() => setPwShow(v => !v)}
                        style={{
                          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', color: 'var(--text3)',
                          cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
                        }}
                      >
                        {pwShow ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 8 }}>
                  <button type="submit" className="btn btn-primary">
                    <CheckIcon />
                    Save Changes
                  </button>
                  {saved && (
                    <div className="save-toast">
                      <CheckIcon />
                      Saved!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>


      {/* ── Company Info + Logo (admin only) ─────────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const cfg = DB.get('appConfig') || {};
        const ci = (typeof cfg === 'object' && !Array.isArray(cfg)) ? (cfg.company_info || {}) : {};
        const [logoUrl, setLogoUrl] = React.useState(ci.logo_url || '');
        const [companyName, setCompanyName] = React.useState(ci.name || '');
        const [companyEmail, setCompanyEmail] = React.useState(ci.email || '');
        const [companyPhone, setCompanyPhone] = React.useState(ci.telephone || ci.phone || '');
        const [companyAddress, setCompanyAddress] = React.useState(ci.address || '');
        const [logoUploading, setLogoUploading] = React.useState(false);
        const [ciSaved, setCiSaved] = React.useState('');
        const logoRef = React.useRef();

        const handleLogoUpload = async (e) => {
          const file = e.target.files[0];
          if (!file || !file.type.startsWith('image/')) return;
          setLogoUploading(true);
          try {
            const reader = new FileReader();
            reader.onload = async (ev) => {
              const url = await uploadImage(ev.target.result);
              if (url) setLogoUrl(url);
              setLogoUploading(false);
            };
            reader.readAsDataURL(file);
          } catch { setLogoUploading(false); }
          e.target.value = '';
        };

        const saveCompanyInfo = () => {
          const allCfg = DB.get('appConfig') || {};
          const base = typeof allCfg === 'object' && !Array.isArray(allCfg) ? allCfg : {};
          const updated = {
            ...(base.company_info || {}),
            logo_url: logoUrl,
            name: companyName,
            email: companyEmail,
            telephone: companyPhone,
            phone: companyPhone,
            address: companyAddress,
          };
          DB.set('appConfig', { ...base, company_info: updated });
          setCiSaved('✅ Saved!');
          setTimeout(() => setCiSaved(''), 3000);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">Company Information</div>
            </div>
            <div className="card-body">
              {/* Logo */}
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                {logoUrl ? (
                  <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <img src={logoUrl} alt="Logo" style={{ height: 50, maxWidth: 220, objectFit: 'contain', display: 'block' }} />
                  </div>
                ) : (
                  <div style={{ padding: '16px 24px', background: 'var(--bg3)', borderRadius: 8, border: '2px dashed var(--border)', color: 'var(--text3)', fontSize: 12 }}>
                    No logo uploaded
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" disabled={logoUploading} onClick={() => logoRef.current?.click()}>
                    {logoUploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </button>
                  {logoUrl && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setLogoUrl('')}>Remove</button>}
                  <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                </div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 14 }}>
                PNG or SVG with transparent background recommended.
              </div>

              {/* Company fields */}
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Company Name</label>
                  <input className="form-input" value={companyName} onChange={e => setCompanyName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input className="form-input" value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button className="btn btn-primary" onClick={saveCompanyInfo}>Save Changes</button>
                {ciSaved && <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>{ciSaved}</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Website Hero Background (admin only) ────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const cfg = DB.get('appConfig') || {};
        const base = (typeof cfg === 'object' && !Array.isArray(cfg)) ? cfg : {};
        const hero = base.website_hero || { type: 'animation', image_url: '', video_url: '', overlay_opacity: 0.7 };
        const [heroType, setHeroType] = React.useState(hero.type || 'animation');
        const [heroImageUrl, setHeroImageUrl] = React.useState(hero.image_url || '');
        const [heroVideoUrl, setHeroVideoUrl] = React.useState(hero.video_url || '');
        const [heroOverlay, setHeroOverlay] = React.useState(hero.overlay_opacity != null ? hero.overlay_opacity : 0.7);
        const [heroUploading, setHeroUploading] = React.useState(false);
        const [heroSaved, setHeroSaved] = React.useState('');
        const heroFileRef = React.useRef();

        const handleHeroUpload = async (e) => {
          const file = e.target.files[0];
          if (!file) return;
          setHeroUploading(true);
          try {
            if (heroType === 'video' && file.type.startsWith('video/')) {
              const url = await uploadImage(file, 'branding');
              if (url) setHeroVideoUrl(url);
            } else if (file.type.startsWith('image/')) {
              const reader = new FileReader();
              reader.onload = async (ev) => {
                const url = await uploadImage(ev.target.result, 'branding');
                if (url) setHeroImageUrl(url);
                setHeroUploading(false);
              };
              reader.readAsDataURL(file);
              e.target.value = '';
              return;
            }
          } catch {}
          setHeroUploading(false);
          e.target.value = '';
        };

        const saveHero = () => {
          const allCfg = DB.get('appConfig') || {};
          const b = typeof allCfg === 'object' && !Array.isArray(allCfg) ? allCfg : {};
          DB.set('appConfig', { ...b, website_hero: { type: heroType, image_url: heroImageUrl, video_url: heroVideoUrl, overlay_opacity: parseFloat(heroOverlay) } });
          setHeroSaved('✅ Saved!');
          setTimeout(() => setHeroSaved(''), 3000);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">Website Hero Background</div>
            </div>
            <div className="card-body">
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
                Customize the homepage hero section background. Choose an image, video, or the default animation.
              </div>

              {/* Type selector */}
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Background Type</label>
                <select className="form-input" value={heroType} onChange={e => setHeroType(e.target.value)} style={{ maxWidth: 220 }}>
                  <option value="animation">Animation (default grid)</option>
                  <option value="image">Image — upload a photo</option>
                  <option value="video">Video — upload an MP4 (loops, muted)</option>
                </select>
              </div>

              {/* Upload area */}
              {(heroType === 'image' || heroType === 'video') && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    {heroType === 'image' && heroImageUrl && (
                      <div style={{ padding: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <img src={heroImageUrl} alt="Hero" style={{ height: 80, maxWidth: 200, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                      </div>
                    )}
                    {heroType === 'video' && heroVideoUrl && (
                      <div style={{ padding: 4, background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <video src={heroVideoUrl} muted style={{ height: 80, maxWidth: 200, borderRadius: 6, display: 'block' }} />
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" disabled={heroUploading} onClick={() => heroFileRef.current?.click()}>
                        {heroUploading ? 'Uploading...' : (heroType === 'image' ? (heroImageUrl ? 'Change Image' : 'Upload Image') : (heroVideoUrl ? 'Change Video' : 'Upload Video'))}
                      </button>
                      {(heroImageUrl || heroVideoUrl) && (
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => { setHeroImageUrl(''); setHeroVideoUrl(''); }}>Remove</button>
                      )}
                      <input ref={heroFileRef} type="file" accept={heroType === 'video' ? 'video/mp4,video/*' : 'image/*'} style={{ display: 'none' }} onChange={handleHeroUpload} />
                    </div>
                  </div>
                </div>
              )}

              {/* Overlay darkness */}
              {(heroType === 'image' || heroType === 'video') && (
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label className="form-label">Overlay Darkness: {Math.round(heroOverlay * 100)}%</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, maxWidth: 300 }}>
                    <input type="range" min="0" max="1" step="0.05" value={heroOverlay} onChange={e => setHeroOverlay(e.target.value)}
                      style={{ flex: 1, accentColor: 'var(--accent)' }} />
                    <select className="form-input" value={heroOverlay} onChange={e => setHeroOverlay(e.target.value)} style={{ width: 80 }}>
                      {[0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map(v => (
                        <option key={v} value={v}>{Math.round(v * 100)}%</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                    Higher = darker overlay. Ensures text remains readable over the background.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button className="btn btn-primary" onClick={saveHero}>Save Hero Settings</button>
                {heroSaved && <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>{heroSaved}</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Notification Preferences (staff only, not customers) ── */}
      {!(user.role === 'customer' || ROLES[user.role]?.isCustomer) && (() => {
        const key = 'users';
        const record = (DB.get(key) || []).find(u => u.id === user.id);
        const prefs = record?.notification_preferences || { push: true, email: true, invoices: true, containers: true, selections: true, quotes: true, messages: false };
        const [notifPrefs, setNotifPrefs] = React.useState(prefs);
        const [notifSaved, setNotifSaved] = React.useState(false);

        const toggle = (field) => setNotifPrefs(prev => ({ ...prev, [field]: !prev[field] }));
        const savePrefs = () => {
          const list = DB.get(key) || [];
          DB.set(key, list.map(u => u.id === user.id ? { ...u, notification_preferences: notifPrefs } : u));
          setNotifSaved(true);
          setTimeout(() => setNotifSaved(false), 2000);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">Notification Preferences</div>
            </div>
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.push} onChange={() => toggle('push')} /> Push Notifications
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.email} onChange={() => toggle('email')} /> Email Notifications
                </label>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, marginBottom: 4 }}>Notify me about:</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.invoices} onChange={() => toggle('invoices')} /> Invoices & Payments
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.containers} onChange={() => toggle('containers')} /> Container & Shipping updates
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.selections} onChange={() => toggle('selections')} /> Selection orders
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.quotes} onChange={() => toggle('quotes')} /> Price quotes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={notifPrefs.messages || false} onChange={() => toggle('messages')} /> General messages
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm" onClick={savePrefs}>Save Preferences</button>
                {notifSaved && <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Email Settings (admin only) ───────────────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const cfg = DB.get('appConfig') || {};
        const es = (typeof cfg === 'object' && !Array.isArray(cfg)) ? (cfg.email_settings || {}) : {};
        const [emailCfg, setEmailCfg] = React.useState({ enabled: es.enabled || false, from_email: es.from_email || 'notifications@bigstar.com', from_name: es.from_name || 'BIG STAR', reply_to: es.reply_to || 'info@bigstar.com' });
        const [emailSaved, setEmailSaved] = React.useState(false);
        const [testResult, setTestResult] = React.useState('');

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">Email Notifications</div>
            </div>
            <div className="card-body">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={emailCfg.enabled} onChange={e => setEmailCfg(prev => ({ ...prev, enabled: e.target.checked }))} />
                Enable email notifications
              </label>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>From email</label>
                  <input className="form-input" value={emailCfg.from_email} onChange={e => setEmailCfg(prev => ({ ...prev, from_email: e.target.value }))} style={{ fontSize: 12 }} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>Reply-to</label>
                  <input className="form-input" value={emailCfg.reply_to} onChange={e => setEmailCfg(prev => ({ ...prev, reply_to: e.target.value }))} style={{ fontSize: 12 }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  const allCfg = DB.get('appConfig') || {};
                  const base = typeof allCfg === 'object' && !Array.isArray(allCfg) ? allCfg : {};
                  DB.set('appConfig', { ...base, email_settings: emailCfg });
                  setEmailSaved(true); setTimeout(() => setEmailSaved(false), 2000);
                }}>Save</button>
                <button className="btn btn-secondary btn-sm" onClick={async () => {
                  setTestResult('Sending...');
                  try {
                    const { supabase } = await import('../data/database');
                    if (!supabase) { setTestResult('No Supabase connection'); return; }
                    const { error } = await supabase.functions.invoke('send-email', {
                      body: { to: user.email, subject: 'Test Email — BIG STAR', html: '<h2>Test email from BIG STAR</h2><p>If you received this, email notifications are working!</p>', from_name: emailCfg.from_name, from_email: emailCfg.from_email },
                    });
                    setTestResult(error ? `Error: ${error.message}` : '✅ Test email sent!');
                  } catch (e) { setTestResult('Edge Function not configured yet'); }
                }}>Send Test Email</button>
                {emailSaved && <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
                {testResult && <span style={{ fontSize: 11, color: testResult.startsWith('✅') ? 'var(--green)' : 'var(--yellow)' }}>{testResult}</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Invoice Numbering (admin/accountant) ────────── */}
      {(user.role === 'admin' || user.role === 'head_admin' || user.role === 'accountant') && (() => {
        const cfg = DB.get('appConfig') || {};
        const counter = (typeof cfg === 'object' && !Array.isArray(cfg)) ? (cfg.invoice_counter || { next_number: 1 }) : { next_number: 1 };
        const [startNum, setStartNum] = React.useState(counter.next_number || 1);
        const [invSaved, setInvSaved] = React.useState(false);

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">Invoice Numbering</div>
            </div>
            <div className="card-body">
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Next invoice number</label>
                <input type="number" className="form-input" value={startNum} onChange={e => setStartNum(Number(e.target.value) || 1)} style={{ maxWidth: 150, fontSize: 16, fontWeight: 700 }} />
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                  Set this to your next invoice number. If your last invoice was #12, enter 13.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-primary btn-sm" onClick={() => {
                  const allCfg = DB.get('appConfig') || {};
                  const base = typeof allCfg === 'object' && !Array.isArray(allCfg) ? allCfg : {};
                  DB.set('appConfig', { ...base, invoice_counter: { next_number: startNum } });
                  setInvSaved(true); setTimeout(() => setInvSaved(false), 2000);
                }}>Save</button>
                {invSaved && <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── QR Code Generation (admin only) ─────────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const [qrStatus, setQrStatus] = React.useState('');
        const [qrRunning, setQrRunning] = React.useState(false);

        const generateMissingQR = async () => {
          setQrRunning(true);
          setQrStatus('Scanning for units without QR codes...');
          let count = 0;

          for (const key of ['vehicles', 'engines', 'spareParts']) {
            const items = DB.get(key) || [];
            const needQR = items.filter(u => u.sku && !u.qr_code_url);
            for (const item of needQR) {
              try {
                const qrDataUrl = await generateBrandedQR(`${window.location.origin}/?u=${item.sku}`, { size: 472 });
                const url = await uploadImage(qrDataUrl);
                if (url) {
                  const list = DB.get(key) || [];
                  DB.set(key, list.map(u => u.id === item.id ? { ...u, qr_code_url: url } : u));
                  count++;
                  setQrStatus(`Generated ${count} QR codes...`);
                }
              } catch {}
            }
          }

          setQrStatus(count > 0 ? `Done! Generated ${count} QR codes.` : 'All units already have QR codes.');
          setQrRunning(false);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div className="card-title">QR Code Management</div>
            </div>
            <div className="card-body">
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
                Generate QR codes for all units that don't have one yet. QR codes encode the unit SKU for quick scanning.
              </div>
              <button className="btn btn-primary" disabled={qrRunning} onClick={generateMissingQR}>
                {qrRunning ? 'Generating...' : 'Generate Missing QR Codes'}
              </button>
              {qrStatus && <div style={{ fontSize: 11, color: 'var(--cyan)', marginTop: 8 }}>{qrStatus}</div>}
            </div>
          </div>
        );
      })()}

      {/* ── Default Fees (admin + accountant) ─────────────── */}
      {(user.role === 'admin' || user.role === 'accountant') && (() => {
        const stored = (() => {
          try {
            const all = DB.get('appConfig');
            if (all && typeof all === 'object' && !Array.isArray(all)) return all.fee_defaults || {};
            if (Array.isArray(all)) { const r = all.find(x => x.key === 'fee_defaults'); return r?.value || {}; }
          } catch {} return {};
        })();
        const fees = { ...{ flat_charge_pct: 8, recycle_fee_pct: 3, vat_pct: 10, nikko_pct: 1, auction_fee_fixed_jpy: 17000, transport_default_jpy: 6000, vanning_default_jpy: 4075 }, ...stored };

        const [editFees, setEditFees] = React.useState(false);
        const [f, setF] = React.useState(fees);
        const [feeSaved, setFeeSaved] = React.useState(false);

        const FEE_FIELDS = [
          { key: 'flat_charge_pct', label: 'Flat Charge', type: '%' },
          { key: 'recycle_fee_pct', label: 'Recycle Fee', type: '%' },
          { key: 'vat_pct', label: 'VAT', type: '%' },
          { key: 'nikko_pct', label: 'Nikko Fee', type: '%' },
          { key: 'auction_fee_fixed_jpy', label: 'Auction Fee', type: '¥' },
          { key: 'transport_default_jpy', label: 'Transport Default', type: '¥' },
          { key: 'vanning_default_jpy', label: 'Vanning Default', type: '¥' },
        ];

        const saveFees = () => {
          DB.set('appConfig', { ...(typeof DB.get('appConfig') === 'object' && !Array.isArray(DB.get('appConfig')) ? DB.get('appConfig') : {}), fee_defaults: f });
          setEditFees(false);
          setFeeSaved(true);
          setTimeout(() => setFeeSaved(false), 2500);
        };

        return (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div className="card-title">Default Fees</div>
              {!editFees && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setEditFees(true)}>Edit</button>}
              {feeSaved && <span className="save-toast"><CheckIcon />Saved!</span>}
            </div>
            <div className="card-body">
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                These defaults auto-fill the pricing form. Accountant can override per unit.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                {FEE_FIELDS.map(ff => (
                  <div key={ff.key} style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{ff.label}</div>
                    {editFees ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="number" className="form-input" value={f[ff.key] ?? ''} onChange={e => setF(prev => ({ ...prev, [ff.key]: e.target.value }))}
                          style={{ fontSize: 13, padding: '4px 6px', flex: 1 }} />
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{ff.type}</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {ff.type === '¥' ? `¥${Number(fees[ff.key] || 0).toLocaleString()}` : `${fees[ff.key] || 0}%`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {editFees && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditFees(false); setF(fees); }}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveFees}><CheckIcon />Save Defaults</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Yard Locations (admin only) ─────────────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const [, bump] = React.useState(0);
        const [newYard, setNewYard] = React.useState({ name: '', city: '', country: '' });
        const [yardSaved, setYardSaved] = React.useState(false);
        const yards = getYardLocations();
        const defaultIds = new Set(DEFAULT_YARD_LOCATIONS.map(y => y.id));

        const addYard = () => {
          if (!newYard.name.trim()) return;
          const id = newYard.name.trim().toLowerCase().replace(/\s+/g, '_') + '_yard';
          const flag = getFlagForCountry(newYard.country.trim());
          const freshBase = DB.get('appConfig') || {};
          const custom = freshBase.custom_yards || [];
          const updated = [...custom, { id, name: newYard.name.trim(), city: newYard.city.trim(), country: newYard.country.trim(), flag }];
          DB.set('appConfig', { ...freshBase, custom_yards: updated });
          setNewYard({ name: '', city: '', country: '' });
          setYardSaved(true); setTimeout(() => setYardSaved(false), 2000);
          bump(v => v + 1);
        };

        const removeYard = (id) => {
          const freshBase = DB.get('appConfig') || {};
          const custom = freshBase.custom_yards || [];
          const updated = custom.filter(y => y.id !== id);
          DB.set('appConfig', { ...freshBase, custom_yards: updated });
          bump(v => v + 1);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">Destination Yards</div></div>
            <div className="card-body">
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Where units are being shipped to. Defaults cannot be removed. Add custom yards below.</div>
              {(() => {
                const grouped = getYardsByCountry();
                const keys = Object.keys(grouped).filter(k => k !== 'Other').sort();
                if (grouped['Other']) keys.push('Other');
                return keys.map(country => (
                  <div key={country} style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', padding: '6px 0 2px' }}>
                      {getFlagForCountry(country)} {country}
                    </div>
                    {grouped[country].map(y => (
                      <div key={y.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                        <span>{y.flag || '📍'}</span>
                        <span style={{ fontWeight: 600, flex: 1 }}>{y.name}</span>
                        {y.city && <span style={{ color: 'var(--text3)', fontSize: 11 }}>{y.city}</span>}
                        {defaultIds.has(y.id)
                          ? <span style={{ fontSize: 10, color: 'var(--text3)' }}>default</span>
                          : <button onClick={() => removeYard(y.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>✕</button>
                        }
                      </div>
                    ))}
                  </div>
                ));
              })()}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Name *</div>
                  <input className="form-input" value={newYard.name} onChange={e => setNewYard(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Pakistan Yard" style={{ width: 140, fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>City</div>
                  <input className="form-input" value={newYard.city} onChange={e => setNewYard(p => ({ ...p, city: e.target.value }))} placeholder="e.g. Karachi" style={{ width: 100, fontSize: 12 }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Country</div>
                  <select className="form-select" value={newYard.country} onChange={e => setNewYard(p => ({ ...p, country: e.target.value }))} style={{ width: 130, fontSize: 12 }}>
                    <option value="">Select</option>
                    {getDestinationCountries().map(c => <option key={c.id} value={c.name}>{c.flag} {c.name}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary btn-sm" onClick={addYard} disabled={!newYard.name.trim()}>+ Add</button>
                {yardSaved && <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Destination Countries (admin only) ─────────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const [, bump] = React.useState(0);
        const [newCountryName, setNewCountryName] = React.useState('');
        const [countrySaved, setCountrySaved] = React.useState(false);
        const allCountries = getDestinationCountries();
        const defaultIds = new Set(DEFAULT_DESTINATION_COUNTRIES.map(c => c.id));

        const addCountry = () => {
          const name = newCountryName.trim();
          if (!name) return;
          const id = name.toLowerCase().replace(/\s+/g, '_');
          if (allCountries.some(c => c.id === id)) { alert('Country "' + name + '" already exists.'); return; }
          const flag = getFlagForCountry(name) || '🌍';
          const freshBase = DB.get('appConfig') || {};
          const custom = freshBase.custom_destination_countries || [];
          const updated = [...custom, { id, name, flag }];
          DB.set('appConfig', { ...freshBase, custom_destination_countries: updated });
          setNewCountryName('');
          setCountrySaved(true); setTimeout(() => setCountrySaved(false), 2000);
          bump(v => v + 1);
        };

        const removeCountry = (id) => {
          const freshBase = DB.get('appConfig') || {};
          const custom = freshBase.custom_destination_countries || [];
          const updated = custom.filter(c => c.id !== id);
          DB.set('appConfig', { ...freshBase, custom_destination_countries: updated });
          bump(v => v + 1);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">Destination Countries</div></div>
            <div className="card-body">
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Countries units can be shipped to. Defaults cannot be removed. Add custom countries below.</div>
              {allCountries.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span>{c.flag}</span>
                  <span style={{ fontWeight: 600, flex: 1 }}>{c.name}</span>
                  {defaultIds.has(c.id)
                    ? <span style={{ fontSize: 10, color: 'var(--text3)' }}>default</span>
                    : <button onClick={() => removeCountry(c.id)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>✕</button>
                  }
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Country Name *</div>
                  <input className="form-input" value={newCountryName} onChange={e => setNewCountryName(e.target.value)} placeholder="e.g. Georgia" style={{ width: 160, fontSize: 12 }} />
                </div>
                <button className="btn btn-primary btn-sm" onClick={addCountry} disabled={!newCountryName.trim()}>+ Add</button>
                {countrySaved && <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Zone Allocations (admin only) ─────────────────── */}
      {(user.role === 'admin' || user.role === 'head_admin') && (() => {
        const [, bump] = React.useState(0);
        const [newLetter, setNewLetter] = React.useState('');
        const [newCountry, setNewCountry] = React.useState('');
        const [zoneSaved, setZoneSaved] = React.useState(false);
        const allocs = getZoneAllocations();
        const countries = getDestinationCountries();
        const countryMeta = (id) => countries.find(c => c.id === id) || { name: id, flag: '🌍' };

        const YARD = 'hokkaido_yard';
        const allocate = () => {
          const letter = newLetter.trim().toUpperCase();
          if (!letter || !/^[A-Z]$/.test(letter)) { alert('Letter must be a single A–Z character.'); return; }
          if (!newCountry) return;
          const freshBase = DB.get('appConfig') || {};
          const byYard = freshBase.zone_allocations_by_yard || {};
          const freshAllocs = byYard[YARD] || {};
          if (freshAllocs[letter]) { alert('Letter ' + letter + ' is already allocated.'); return; }
          const existingLetter = Object.keys(freshAllocs).sort().find(k => freshAllocs[k] === newCountry);
          if (existingLetter) {
            if (!window.confirm('Country already allocated to letter ' + existingLetter + '. Continue?')) return;
          }
          const merged = { ...freshAllocs, [letter]: newCountry };
          DB.set('appConfig', { ...freshBase, zone_allocations_by_yard: { ...byYard, [YARD]: merged } });
          setNewLetter(''); setNewCountry('');
          setZoneSaved(true); setTimeout(() => setZoneSaved(false), 2000);
          bump(v => v + 1);
        };

        const removeZone = (letter) => {
          const freshBase = DB.get('appConfig') || {};
          const byYard = freshBase.zone_allocations_by_yard || {};
          const freshAllocs = { ...(byYard[YARD] || {}) };
          delete freshAllocs[letter];
          DB.set('appConfig', { ...freshBase, zone_allocations_by_yard: { ...byYard, [YARD]: freshAllocs } });
          bump(v => v + 1);
        };

        return (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><div className="card-title">Zone Allocations</div></div>
            <div className="card-body">
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>Allocate yard zone letters to destination countries. Each letter covers two sub-zones: 1 = movable, 2 = not-movable.</div>
              {Object.keys(allocs).sort().map(letter => {
                const m = countryMeta(allocs[letter]);
                return (
                  <div key={letter} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, minWidth: 24, textAlign: 'center' }}>{letter}</span>
                    <span style={{ color: 'var(--text3)' }}>→</span>
                    <span>{m.flag}</span>
                    <span style={{ fontWeight: 600, flex: 1 }}>{m.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>{letter}1 movable · {letter}2 not-movable</span>
                    <button onClick={() => removeZone(letter)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14 }}>✕</button>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Letter *</div>
                  <input className="form-input" value={newLetter} onChange={e => setNewLetter(e.target.value.toUpperCase().slice(0, 1))} placeholder="A" maxLength={1} style={{ width: 48, fontSize: 12, textAlign: 'center', textTransform: 'uppercase' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Country *</div>
                  <select className="form-select" value={newCountry} onChange={e => setNewCountry(e.target.value)} style={{ width: 160, fontSize: 12 }}>
                    <option value="">Select…</option>
                    {countries.map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
                  </select>
                </div>
                <button className="btn btn-primary btn-sm" onClick={allocate} disabled={!newLetter.trim() || !newCountry}>+ Allocate</button>
                {zoneSaved && <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Saved</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bank Details (admin + accountant) ─────────────── */}
      {(user.role === 'admin' || user.role === 'accountant') && (() => {
        const stored = (() => {
          try {
            const all = DB.get('appConfig');
            if (all && typeof all === 'object' && !Array.isArray(all)) return all.bank_details || {};
          } catch {} return {};
        })();

        const [editBank, setEditBank] = React.useState(false);
        const [b, setB] = React.useState({ bank_name: '', account_name: '', account_number: '', iban: '', swift_code: '', branch: '', currency: 'USD', ...stored });
        const [bankSaved, setBankSaved] = React.useState(false);

        const BANK_FIELDS = [
          { key: 'bank_name', label: 'Bank Name' },
          { key: 'account_name', label: 'Account Name' },
          { key: 'account_number', label: 'Account Number' },
          { key: 'iban', label: 'IBAN' },
          { key: 'swift_code', label: 'SWIFT / BIC' },
          { key: 'branch', label: 'Branch' },
          { key: 'currency', label: 'Currency' },
        ];

        const saveBank = () => {
          const cfg = typeof DB.get('appConfig') === 'object' && !Array.isArray(DB.get('appConfig')) ? DB.get('appConfig') : {};
          DB.set('appConfig', { ...cfg, bank_details: b });
          setEditBank(false);
          setBankSaved(true);
          setTimeout(() => setBankSaved(false), 2500);
        };

        return (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div className="card-title">Bank Details</div>
              {!editBank && <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => setEditBank(true)}>Edit</button>}
              {bankSaved && <span className="save-toast"><CheckIcon />Saved!</span>}
            </div>
            <div className="card-body">
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                These details appear on all invoices automatically.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {BANK_FIELDS.map(f => (
                  <div key={f.key} style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{f.label}</div>
                    {editBank ? (
                      <input className="form-input" value={b[f.key] || ''} onChange={e => setB(prev => ({ ...prev, [f.key]: e.target.value }))}
                        style={{ fontSize: 13, padding: '4px 6px' }} />
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{stored[f.key] || '—'}</div>
                    )}
                  </div>
                ))}
              </div>
              {editBank && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditBank(false); setB({ ...stored }); }}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveBank}><CheckIcon />Save Bank Details</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Parts Catalog Management (admin) ────────────────── */}
      {user.role === 'admin' && <PartsCatalogManager />}

      {/* ── Data Management (tech admin only — U1) ─────────── */}
      {user.id === 'U1' && (
        <div className="settings-grid">
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                <DownloadIcon />
                {t('export')} Data
              </div>
            </div>
            <div className="card-body">
              <p style={{ marginBottom: 16, color: 'var(--text2)' }}>
                Download a backup of all your data as a JSON file.
              </p>
              <button className="btn btn-primary" onClick={handleExport}>
                <DownloadIcon />
                Export JSON
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ color: 'var(--red)' }}>
                <XIcon />
                Reset Data
              </div>
            </div>
            <div className="card-body">
              <p style={{ marginBottom: 16, color: 'var(--text2)' }}>
                Clear all data and start fresh. This cannot be undone.
              </p>
              <button
                className="btn btn-secondary"
                style={{ borderColor: 'var(--red)', color: 'var(--red)' }}
                onClick={handleReset}
              >
                <XIcon />
                Reset All Data
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Parts Catalog Manager ────────────────────────────────────
function PartsCatalogManager() {
  const { t } = useLanguage();
  const [catalog, setCatalog] = useState(() => DB.get('partsCatalog') || PARTS_CATALOG);
  const [search, setSearch] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);
  const [editPart, setEditPart] = useState(null); // { index, ...part } or null
  const [addMode, setAddMode] = useState(false);
  const [newPart, setNewPart] = useState({ part_name: '', category: '', is_common: false, display_order: 0 });
  const [saved, setSaved] = useState(false);

  const categories = [...new Set(catalog.map(p => p.category))];
  // Preserve PARTS_CATALOG_CATEGORIES order, then any new ones
  const orderedCats = [
    ...PARTS_CATALOG_CATEGORIES.filter(c => categories.includes(c)),
    ...categories.filter(c => !PARTS_CATALOG_CATEGORIES.includes(c)),
  ];

  const q = search.toLowerCase();
  const filteredCatalog = q
    ? catalog.filter(p => p.part_name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q))
    : catalog;

  const groupedFiltered = {};
  filteredCatalog.forEach(p => {
    if (!groupedFiltered[p.category]) groupedFiltered[p.category] = [];
    groupedFiltered[p.category].push(p);
  });
  Object.values(groupedFiltered).forEach(parts =>
    parts.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
  );

  const visibleCats = orderedCats.filter(c => groupedFiltered[c]?.length > 0);

  const persist = (updated) => {
    setCatalog(updated);
    DB.set('partsCatalog', updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleDelete = (partName) => {
    if (!window.confirm(`Delete "${partName}" from the catalog?`)) return;
    persist(catalog.filter(p => p.part_name !== partName));
  };

  const handleToggleCommon = (partName) => {
    persist(catalog.map(p => p.part_name === partName ? { ...p, is_common: !p.is_common } : p));
  };

  const handleSaveEdit = () => {
    if (!editPart?.part_name?.trim()) return;
    persist(catalog.map((p, i) => i === editPart.index ? {
      ...p,
      part_name: editPart.part_name.trim(),
      category: editPart.category || p.category,
      display_order: Number(editPart.display_order) || p.display_order,
      is_common: editPart.is_common,
    } : p));
    setEditPart(null);
  };

  const handleAdd = () => {
    if (!newPart.part_name?.trim() || !newPart.category?.trim()) return;
    const catParts = catalog.filter(p => p.category === newPart.category);
    const maxOrder = catParts.reduce((m, p) => Math.max(m, p.display_order || 0), 0);
    persist([...catalog, {
      part_name: newPart.part_name.trim(),
      category: newPart.category.trim(),
      display_order: Number(newPart.display_order) || maxOrder + 1,
      is_common: newPart.is_common,
    }]);
    setNewPart({ part_name: '', category: '', is_common: false, display_order: 0 });
    setAddMode(false);
  };

  const movePartOrder = (partName, dir) => {
    const idx = catalog.findIndex(p => p.part_name === partName);
    if (idx < 0) return;
    const part = catalog[idx];
    const catParts = catalog.filter(p => p.category === part.category).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const catIdx = catParts.findIndex(p => p.part_name === partName);
    const swapIdx = catIdx + dir;
    if (swapIdx < 0 || swapIdx >= catParts.length) return;
    const swapPart = catParts[swapIdx];
    const tmpOrder = part.display_order;
    persist(catalog.map(p => {
      if (p.part_name === partName) return { ...p, display_order: swapPart.display_order };
      if (p.part_name === swapPart.part_name) return { ...p, display_order: tmpOrder };
      return p;
    }));
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">Parts Catalog</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saved && <span className="save-toast"><CheckIcon /> Saved!</span>}
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{catalog.length} parts</span>
          <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} onClick={() => setAddMode(true)}>
            <PlusIcon /> Add Part
          </button>
        </div>
      </div>
      <div className="card-body" style={{ padding: 0 }}>

        {/* Search */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }}><SearchIcon /></span>
            <input className="form-input" placeholder="Search parts..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 32, fontSize: 13 }} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 14 }}>×</button>}
          </div>
        </div>

        {/* Add Part form */}
        {addMode && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'rgba(88,166,255,0.04)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Add New Part</div>
            <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr auto auto' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Part Name *</label>
                <input className="form-input" value={newPart.part_name} onChange={e => setNewPart(p => ({ ...p, part_name: e.target.value }))} placeholder="e.g. Turbo Intercooler" />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Category *</label>
                <input className="form-input" list="cat-list" value={newPart.category} onChange={e => setNewPart(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Engine & Transmission" />
                <datalist id="cat-list">{orderedCats.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="form-group" style={{ alignSelf: 'end' }}>
                <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--text2)' }}>
                  <input type="checkbox" checked={newPart.is_common} onChange={e => setNewPart(p => ({ ...p, is_common: e.target.checked }))} /> Common
                </label>
              </div>
              <div className="form-group" style={{ alignSelf: 'end', display: 'flex', gap: 4 }}>
                <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!newPart.part_name.trim() || !newPart.category.trim()}>
                  <CheckIcon /> Add
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setAddMode(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Categories + parts */}
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          {visibleCats.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No parts match "{search}"</div>
          )}
          {visibleCats.map(cat => {
            const isExpanded = expandedCat === cat || !!q;
            const parts = groupedFiltered[cat] || [];
            return (
              <div key={cat} style={{ borderBottom: '1px solid var(--border)' }}>
                <div
                  onClick={() => !q && setExpandedCat(isExpanded ? null : cat)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', cursor: 'pointer', background: 'var(--bg3)', fontSize: 12, fontWeight: 600, userSelect: 'none' }}
                >
                  <span style={{ fontSize: 8, width: 12, color: 'var(--text3)' }}>{isExpanded ? '▼' : '▶'}</span>
                  <span style={{ flex: 1 }}>{cat}</span>
                  <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>({parts.length})</span>
                </div>
                {isExpanded && parts.map((part, i) => {
                  const globalIdx = catalog.findIndex(p => p.part_name === part.part_name && p.category === part.category);
                  const isEditing = editPart?.index === globalIdx;
                  return (
                    <div key={part.part_name} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 16px 5px 28px',
                      borderBottom: i < parts.length - 1 ? '1px solid var(--border)' : 'none',
                      fontSize: 12, background: isEditing ? 'rgba(88,166,255,0.06)' : 'transparent',
                    }}>
                      {isEditing ? (
                        <>
                          <input className="form-input" value={editPart.part_name} onChange={e => setEditPart(p => ({ ...p, part_name: e.target.value }))}
                            style={{ flex: 1, fontSize: 12, padding: '3px 6px' }} />
                          <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', color: 'var(--text2)', flexShrink: 0 }}>
                            <input type="checkbox" checked={editPart.is_common} onChange={e => setEditPart(p => ({ ...p, is_common: e.target.checked }))} /> Common
                          </label>
                          <input type="number" value={editPart.display_order} onChange={e => setEditPart(p => ({ ...p, display_order: e.target.value }))}
                            style={{ width: 40, fontSize: 10, padding: '2px 4px', textAlign: 'center', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }}
                            title="Display order" />
                          <button className="btn btn-primary btn-sm" style={{ padding: '2px 8px', fontSize: 10 }} onClick={handleSaveEdit}><CheckIcon /></button>
                          <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 10 }} onClick={() => setEditPart(null)}>×</button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, color: 'var(--text2)' }}>{part.part_name}</span>
                          {part.is_common && (
                            <span style={{ fontSize: 8, background: 'rgba(63,185,80,0.15)', color: 'var(--green)', padding: '1px 5px', borderRadius: 4, fontWeight: 700, flexShrink: 0, cursor: 'pointer' }}
                              onClick={() => handleToggleCommon(part.part_name)} title="Click to toggle common">
                              Common
                            </span>
                          )}
                          {!part.is_common && (
                            <span style={{ fontSize: 8, color: 'var(--text3)', cursor: 'pointer', flexShrink: 0 }}
                              onClick={() => handleToggleCommon(part.part_name)} title="Click to mark as common">
                              ○
                            </span>
                          )}
                          <span style={{ fontSize: 9, color: 'var(--text3)', width: 20, textAlign: 'center', flexShrink: 0 }}>{part.display_order}</span>
                          <button onClick={() => movePartOrder(part.part_name, -1)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 10, padding: '0 2px' }} title="Move up">▲</button>
                          <button onClick={() => movePartOrder(part.part_name, 1)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 10, padding: '0 2px' }} title="Move down">▼</button>
                          <button onClick={() => setEditPart({ ...part, index: globalIdx })} style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', padding: '0 2px' }} title="Edit"><EditIcon /></button>
                          <button onClick={() => handleDelete(part.part_name)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: '0 2px' }} title="Delete"><DeleteIcon /></button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Settings;
